/**
 * Issue Codes — Canonical registry of all issue codes emitted by pipeline stages.
 *
 * Split from issueContract.ts (C1, Sprint 5). This is the SSOT for:
 *  - ISSUE_CODES (registry)
 *  - IssueCode (string union derived from registry)
 *  - IssueKind (blocker | coach | info)
 *  - IssueDomain (pipeline stage domain)
 *  - PipelineStage (1-7)
 *
 * Provides compile-time safety and documentation for each code.
 * RESOLVES: CONFLICT-11 (Issue codes defined but never enumerated)
 */

// ─── Issue Kinds ──────────────────────────────────────────────────────────────

/** Issue kind determines gate behavior */
export type IssueKind = 'blocker' | 'coach' | 'info'

// ─── Issue Code Registry ──────────────────────────────────────────────────────

export const ISSUE_CODES = {
  // ── Schema Stage (Stage 1) ──────────────────────────────────────────────
  SCHEMA_MISSING_ID: 'SCHEMA_MISSING_ID',
  SCHEMA_MISSING_FAMILY_ID: 'SCHEMA_MISSING_FAMILY_ID',
  SCHEMA_MISSING_MODULE: 'SCHEMA_MISSING_MODULE',
  SCHEMA_MISSING_QUESTION_TEXT: 'SCHEMA_MISSING_QUESTION_TEXT',
  SCHEMA_MISSING_QUESTION_TYPE: 'SCHEMA_MISSING_QUESTION_TYPE',
  SCHEMA_UNKNOWN_FAMILY: 'SCHEMA_UNKNOWN_FAMILY',
  SCHEMA_MODULE_MISMATCH: 'SCHEMA_MODULE_MISMATCH',
  SCHEMA_TIER_MISMATCH: 'SCHEMA_TIER_MISMATCH',
  SCHEMA_SKILL_MISMATCH: 'SCHEMA_SKILL_MISMATCH',
  SCHEMA_SUBSKILL_MISMATCH: 'SCHEMA_SUBSKILL_MISMATCH',
  SCHEMA_MC_MISSING_OPTIONS: 'SCHEMA_MC_MISSING_OPTIONS',
  SCHEMA_MC_EMPTY_OPTION: 'SCHEMA_MC_EMPTY_OPTION',
  SCHEMA_MC_DUPLICATE_OPTIONS: 'SCHEMA_MC_DUPLICATE_OPTIONS',
  SCHEMA_MC_MISSING_CORRECT_ANSWER: 'SCHEMA_MC_MISSING_CORRECT_ANSWER',
  SCHEMA_MC_INVALID_CORRECT_ANSWER: 'SCHEMA_MC_INVALID_CORRECT_ANSWER',
  SCHEMA_MISSING_EXPLANATION: 'SCHEMA_MISSING_EXPLANATION',
  SCHEMA_ID_FORMAT: 'SCHEMA_ID_FORMAT',
  SCHEMA_MC_TOO_FEW_OPTIONS: 'SCHEMA_MC_TOO_FEW_OPTIONS',
  SCHEMA_MC_TOO_MANY_OPTIONS: 'SCHEMA_MC_TOO_MANY_OPTIONS',
  SCHEMA_FILL_IN_MISSING_ACCEPTED: 'SCHEMA_FILL_IN_MISSING_ACCEPTED',
  SCHEMA_FILL_IN_EMPTY_ANSWER: 'SCHEMA_FILL_IN_EMPTY_ANSWER',
  SCHEMA_MISSING_CONCEPT_ID: 'SCHEMA_MISSING_CONCEPT_ID',
  SCHEMA_MISSING_VARIANT_INDEX: 'SCHEMA_MISSING_VARIANT_INDEX',

  // ── Type Stage (Stage 1b) ──────────────────────────────────────────────
  TYPE_UNKNOWN: 'TYPE_UNKNOWN',
  TYPE_DEPRECATED_INTERACTIVE_CONFIG: 'TYPE_DEPRECATED_INTERACTIVE_CONFIG',
  TYPE_MISSING_DATA: 'TYPE_MISSING_DATA',
  TYPE_MISSING_DATA_FIELD: 'TYPE_MISSING_DATA_FIELD',
  // Generic mode validation (data.mode whitelist — see questionModes.ts)
  TYPE_INVALID_MODE: 'TYPE_INVALID_MODE',
  TYPE_MODE_NOT_SUPPORTED: 'TYPE_MODE_NOT_SUPPORTED',
  // Clock
  TYPE_CLOCK_INVALID_MODE: 'TYPE_CLOCK_INVALID_MODE',
  TYPE_CLOCK_INVALID_HOURS: 'TYPE_CLOCK_INVALID_HOURS',
  TYPE_CLOCK_INVALID_MINUTES: 'TYPE_CLOCK_INVALID_MINUTES',
  TYPE_CLOCK_MC_MISSING_OPTIONS: 'TYPE_CLOCK_MC_MISSING_OPTIONS',
  // Money
  TYPE_MONEY_INVALID_MODE: 'TYPE_MONEY_INVALID_MODE',
  TYPE_MONEY_INVALID_TARGET: 'TYPE_MONEY_INVALID_TARGET',
  TYPE_MONEY_INVALID_DENOM: 'TYPE_MONEY_INVALID_DENOM',
  TYPE_MONEY_INVALID_DENOM_VALUE: 'TYPE_MONEY_INVALID_DENOM_VALUE',
  TYPE_MONEY_INVALID_DENOM_TYPE: 'TYPE_MONEY_INVALID_DENOM_TYPE',
  TYPE_MONEY_DENOMS_NOT_ARRAY: 'TYPE_MONEY_DENOMS_NOT_ARRAY',
  TYPE_MONEY_MISSING_PRICE: 'TYPE_MONEY_MISSING_PRICE',
  TYPE_MONEY_MISSING_PAID: 'TYPE_MONEY_MISSING_PAID',
  TYPE_MONEY_PAID_LESS_THAN_PRICE: 'TYPE_MONEY_PAID_LESS_THAN_PRICE',
  // Ordering
  TYPE_ORDERING_LENGTH_MISMATCH: 'TYPE_ORDERING_LENGTH_MISMATCH',
  TYPE_ORDERING_INVALID_PERMUTATION: 'TYPE_ORDERING_INVALID_PERMUTATION',
  TYPE_ORDERING_EMPTY_ITEM: 'TYPE_ORDERING_EMPTY_ITEM',
  // Matching
  TYPE_MATCHING_TOO_FEW: 'TYPE_MATCHING_TOO_FEW',
  TYPE_MATCHING_INVALID_PAIR: 'TYPE_MATCHING_INVALID_PAIR',
  TYPE_MATCHING_EMPTY_LEFT: 'TYPE_MATCHING_EMPTY_LEFT',
  TYPE_MATCHING_EMPTY_RIGHT: 'TYPE_MATCHING_EMPTY_RIGHT',
  TYPE_MATCHING_NO_DATA: 'TYPE_MATCHING_NO_DATA',
  TYPE_MATCHING_INVALID_LEFT_IDX: 'TYPE_MATCHING_INVALID_LEFT_IDX',
  TYPE_MATCHING_INVALID_RIGHT_IDX: 'TYPE_MATCHING_INVALID_RIGHT_IDX',
  // Table/Schedule
  TYPE_TABLE_ROW_NOT_ARRAY: 'TYPE_TABLE_ROW_NOT_ARRAY',
  TYPE_TABLE_ROW_LENGTH_MISMATCH: 'TYPE_TABLE_ROW_LENGTH_MISMATCH',
  TYPE_SCHEDULE_ROW_NOT_ARRAY: 'TYPE_SCHEDULE_ROW_NOT_ARRAY',
  TYPE_SCHEDULE_ROW_LENGTH_MISMATCH: 'TYPE_SCHEDULE_ROW_LENGTH_MISMATCH',
  // Charts
  TYPE_BAR_CHART_INVALID_ENTRY: 'TYPE_BAR_CHART_INVALID_ENTRY',
  TYPE_BAR_CHART_EMPTY_LABEL: 'TYPE_BAR_CHART_EMPTY_LABEL',
  TYPE_BAR_CHART_VALUE_NOT_NUMBER: 'TYPE_BAR_CHART_VALUE_NOT_NUMBER',
  TYPE_BAR_CHART_TOO_FEW: 'TYPE_BAR_CHART_TOO_FEW',
  TYPE_BAR_CHART_LENGTH_MISMATCH: 'TYPE_BAR_CHART_LENGTH_MISMATCH',
  TYPE_BAR_CHART_NO_DATA: 'TYPE_BAR_CHART_NO_DATA',
  TYPE_LINE_CHART_INVALID_ENTRY: 'TYPE_LINE_CHART_INVALID_ENTRY',
  TYPE_LINE_CHART_VALUE_NOT_NUMBER: 'TYPE_LINE_CHART_VALUE_NOT_NUMBER',
  TYPE_LINE_CHART_LENGTH_MISMATCH: 'TYPE_LINE_CHART_LENGTH_MISMATCH',
  TYPE_LINE_CHART_NO_DATA: 'TYPE_LINE_CHART_NO_DATA',
  TYPE_PIE_CHART_LENGTH_MISMATCH: 'TYPE_PIE_CHART_LENGTH_MISMATCH',
  TYPE_PIE_CHART_VALUE_NOT_NUMBER: 'TYPE_PIE_CHART_VALUE_NOT_NUMBER',
  TYPE_PIE_CHART_EMPTY_LABEL: 'TYPE_PIE_CHART_EMPTY_LABEL',
  TYPE_PIE_CHART_INVALID_CORRECT_INDEX: 'TYPE_PIE_CHART_INVALID_CORRECT_INDEX',
  TYPE_PIE_CHART_MISSING_CORRECT_INDEX: 'TYPE_PIE_CHART_MISSING_CORRECT_INDEX',
  TYPE_PIE_CHART_TOO_FEW_SLICES: 'TYPE_PIE_CHART_TOO_FEW_SLICES',
  // Ratio table / Number sequence
  TYPE_RATIO_TABLE_HEADERS: 'TYPE_RATIO_TABLE_HEADERS',
  TYPE_RATIO_TABLE_ROW_FORMAT: 'TYPE_RATIO_TABLE_ROW_FORMAT',
  TYPE_RATIO_TABLE_MISSING_CELL: 'TYPE_RATIO_TABLE_MISSING_CELL',
  TYPE_RATIO_TABLE_NO_ANSWER: 'TYPE_RATIO_TABLE_NO_ANSWER',
  TYPE_NUMBER_SEQ_TOO_SHORT: 'TYPE_NUMBER_SEQ_TOO_SHORT',
  TYPE_NUMBER_SEQ_INVALID_INDEX: 'TYPE_NUMBER_SEQ_INVALID_INDEX',
  TYPE_NUMBER_SEQ_NO_ANSWER: 'TYPE_NUMBER_SEQ_NO_ANSWER',
  // Text highlight
  TYPE_TEXT_HIGHLIGHT_TOO_FEW_SEGMENTS: 'TYPE_TEXT_HIGHLIGHT_TOO_FEW_SEGMENTS',
  TYPE_TEXT_HIGHLIGHT_NO_CORRECT_SEGMENT:
    'TYPE_TEXT_HIGHLIGHT_NO_CORRECT_SEGMENT',
  TYPE_TEXT_HIGHLIGHT_INVALID_SEGMENT: 'TYPE_TEXT_HIGHLIGHT_INVALID_SEGMENT',
  TYPE_TEXT_HIGHLIGHT_SEGMENT_NO_TEXT: 'TYPE_TEXT_HIGHLIGHT_SEGMENT_NO_TEXT',
  TYPE_TEXT_HIGHLIGHT_NO_DATA: 'TYPE_TEXT_HIGHLIGHT_NO_DATA',
  TYPE_TEXT_HIGHLIGHT_INVALID_RANGE: 'TYPE_TEXT_HIGHLIGHT_INVALID_RANGE',
  TYPE_TEXT_HIGHLIGHT_OUT_OF_RANGE: 'TYPE_TEXT_HIGHLIGHT_OUT_OF_RANGE',
  // Drag drop fill
  TYPE_DRAG_DROP_NO_TEMPLATE: 'TYPE_DRAG_DROP_NO_TEMPLATE',
  TYPE_DRAG_DROP_NO_PLACEHOLDERS: 'TYPE_DRAG_DROP_NO_PLACEHOLDERS',
  TYPE_DRAG_DROP_EMPTY_DRAGGABLE: 'TYPE_DRAG_DROP_EMPTY_DRAGGABLE',
  TYPE_DRAG_DROP_INVALID_PLACEMENT: 'TYPE_DRAG_DROP_INVALID_PLACEMENT',
  // Fill in
  TYPE_FILL_IN_NUMERIC_ANSWER: 'TYPE_FILL_IN_NUMERIC_ANSWER',
  TYPE_FILL_IN_REDUNDANT_CORRECT_ANSWER:
    'TYPE_FILL_IN_REDUNDANT_CORRECT_ANSWER',
  // Column arithmetic
  TYPE_COLUMN_NEGATIVE_OPERAND: 'TYPE_COLUMN_NEGATIVE_OPERAND',
  TYPE_COLUMN_SUB_NEGATIVE_RESULT: 'TYPE_COLUMN_SUB_NEGATIVE_RESULT',
  TYPE_COLUMN_NON_INTEGER: 'TYPE_COLUMN_NON_INTEGER',

  // ── Stub validators (Ruis-audit v1.6 T2.2) ─────────────────────────────
  // Minimal structural checks for previously unvalidated specialized types.
  // Catches missing/invalid data.{type} namespace so render-time
  // "⚠️ Configuratie ontbreekt" amber-card fallbacks are caught early.
  TYPE_SQUARE_ROOT_BUILDER_INVALID_CONFIG:
    'TYPE_SQUARE_ROOT_BUILDER_INVALID_CONFIG',
  TYPE_PLACE_VALUE_BUILD_INVALID_CONFIG:
    'TYPE_PLACE_VALUE_BUILD_INVALID_CONFIG',
  TYPE_BAR_MODEL_PART_INVALID_CONFIG: 'TYPE_BAR_MODEL_PART_INVALID_CONFIG',
  TYPE_STRATEGY_SOLVER_INVALID_CONFIG: 'TYPE_STRATEGY_SOLVER_INVALID_CONFIG',

  // ── Graph Stage (Stage 2) ──────────────────────────────────────────────
  GRAPH_UNKNOWN_PREREQUISITE: 'GRAPH_UNKNOWN_PREREQUISITE',
  GRAPH_CYCLE_DETECTED: 'GRAPH_CYCLE_DETECTED',
  GRAPH_ORPHAN_NODE: 'GRAPH_ORPHAN_NODE',
  GRAPH_TIER_MISMATCH: 'GRAPH_TIER_MISMATCH',
  GRAPH_FAMILY_NOT_IN_LDG: 'GRAPH_FAMILY_NOT_IN_LDG',
  GRAPH_NO_CONTENT: 'GRAPH_NO_CONTENT',
  GRAPH_DEEP_PREREQUISITE_CHAIN: 'GRAPH_DEEP_PREREQUISITE_CHAIN',

  // ── Content Stage (Stage 3) ────────────────────────────────────────────
  CONTENT_FORBIDDEN_VOCAB: 'CONTENT_FORBIDDEN_VOCAB',
  CONTENT_QUESTION_TOO_LONG: 'CONTENT_QUESTION_TOO_LONG',
  CONTENT_CONTEXT_TOO_LONG: 'CONTENT_CONTEXT_TOO_LONG',
  CONTENT_EXPLANATION_TOO_SHORT: 'CONTENT_EXPLANATION_TOO_SHORT',
  CONTENT_GENERIC_EXPLANATION: 'CONTENT_GENERIC_EXPLANATION',
  CONTENT_EXPLANATION_NOT_DIDACTIC: 'CONTENT_EXPLANATION_NOT_DIDACTIC',
  CONTENT_JARGON_DETECTED: 'CONTENT_JARGON_DETECTED',
  CONTENT_EXPLANATION_PASSIVE: 'CONTENT_EXPLANATION_PASSIVE',
  CONTENT_SENTENCE_TOO_LONG: 'CONTENT_SENTENCE_TOO_LONG',
  CONTENT_MISSING_RULE_REF: 'CONTENT_MISSING_RULE_REF',
  CONTENT_MISSING_CHECK_STRATEGY: 'CONTENT_MISSING_CHECK_STRATEGY',
  /**
   * ROLLUP (Sprint 6 — distractor-quality refactor): All distractor-quality
   * findings are now emitted under a single canonical code with
   * `metadata.reason` carrying the discriminator. Replaces seven legacy codes
   * (CONTENT_MISSING_DISTRACTOR_EXPLANATION, CONTENT_DISTRACTOR_TOO_SHORT,
   * CONTENT_GENERIC_DISTRACTOR, CONTENT_NO_MISCONCEPTION_REF,
   * CONTENT_WEAK_DISTRACTOR_SCALE, CONTENT_WEAK_DISTRACTOR_IMPLAUSIBLE,
   * CONTENT_WEAK_DISTRACTOR_OUTLIER).
   *
   * `metadata.reason` ∈ {
   *   'missing_explanation'  — feedback.distractorExplanations[i] absent
   *                            (blocker at tier ≥ 9, coach otherwise)
   *   'too_short'            — distractor explanation below min length (info)
   *   'generic'              — distractor reads as boilerplate, not specific
   *                            misconception (coach)
   *   'no_misconception_ref' — no distractor names a module-specific
   *                            misconception (coach)
   *   'weak_scale'           — numeric correct stands out by magnitude (coach)
   *   'weak_implausible'     — language distractor uses visually impossible
   *                            patterns (coach)
   *   'weak_outlier'         — correct answer is a length/format/numeric/
   *                            similarity outlier (coach)
   * }
   *
   * Admin UI groups primary by `code`, secondary by `metadata.reason`.
   * See computeCodeFrequencyReport — `reasonBreakdown` field.
   */
  CONTENT_DISTRACTOR_QUALITY: 'CONTENT_DISTRACTOR_QUALITY',
  // NOTE: the seven legacy distractor codes below were removed in the rollup.
  // Old snapshots that contain them will still render in diffSnapshots (as
  // "vanished"). Their semantics now live as `metadata.reason` on
  // CONTENT_DISTRACTOR_QUALITY.
  //   CONTENT_MISSING_DISTRACTOR_EXPLANATION → reason: 'missing_explanation'
  //   CONTENT_DISTRACTOR_TOO_SHORT           → reason: 'too_short'
  //   CONTENT_GENERIC_DISTRACTOR             → reason: 'generic'
  //   CONTENT_NO_MISCONCEPTION_REF           → reason: 'no_misconception_ref'
  //   CONTENT_WEAK_DISTRACTOR_SCALE          → reason: 'weak_scale'
  //   CONTENT_WEAK_DISTRACTOR_IMPLAUSIBLE    → reason: 'weak_implausible'
  //   CONTENT_WEAK_DISTRACTOR_OUTLIER        → reason: 'weak_outlier'
  CONTENT_MISSING_LATEX: 'CONTENT_MISSING_LATEX',
  CONTENT_DOT_DECIMAL: 'CONTENT_DOT_DECIMAL',
  CONTENT_EURO_DOT: 'CONTENT_EURO_DOT',
  CONTENT_MISSING_SUPERSCRIPT: 'CONTENT_MISSING_SUPERSCRIPT',
  CONTENT_MISSING_SOLUTION_STEPS: 'CONTENT_MISSING_SOLUTION_STEPS',
  /**
   * ROLLUP (Sprint 6 — CITO refactor): All CITO-parameter findings are now
   * emitted under a single canonical code with `metadata.reason` carrying
   * the discriminator. Replaces six legacy codes (CONTENT_MISSING_CITO_*,
   * CONTENT_CITO_*_OUT_OF_RANGE, CONTENT_CITO_*_BAND_MISMATCH).
   *
   * `metadata.reason` ∈ {
   *   'missing_difficulty'         — citoDifficulty undefined (coach for
   *                                  assessment-eligible types, info otherwise)
   *   'missing_discrimination'     — citoDiscrimination undefined (idem)
   *   'difficulty_out_of_range'    — citoDifficulty outside [0.10, 0.85] (coach)
   *   'difficulty_band_mismatch'   — citoDifficulty outside tier-band range (info)
   *   'discrimination_out_of_range' — citoDiscrimination outside [0.40, 0.95] (coach)
   *   'discrimination_band_mismatch' — citoDiscrimination outside [0.60, 0.85] (info)
   * }
   *
   * Admin UI groups primary by `code`, secondary by `metadata.reason`.
   * See computeCodeFrequencyReport — `reasonBreakdown` field.
   */
  CONTENT_CITO_ISSUE: 'CONTENT_CITO_ISSUE',
  // NOTE: the six legacy CITO codes below were removed in the rollup refactor.
  // Old snapshots that contain them will still render in diffSnapshots (as
  // "vanished"). Their semantics now live as `metadata.reason` on CONTENT_CITO_ISSUE.
  //   CONTENT_MISSING_CITO_DIFFICULTY        → reason: 'missing_difficulty'
  //   CONTENT_MISSING_CITO_DISCRIMINATION    → reason: 'missing_discrimination'
  //   CONTENT_CITO_DIFFICULTY_OUT_OF_RANGE   → reason: 'difficulty_out_of_range'
  //   CONTENT_CITO_DIFFICULTY_BAND_MISMATCH  → reason: 'difficulty_band_mismatch'
  //   CONTENT_CITO_DISCRIMINATION_OUT_OF_RANGE  → reason: 'discrimination_out_of_range'
  //   CONTENT_CITO_DISCRIMINATION_BAND_MISMATCH → reason: 'discrimination_band_mismatch'
  CONTENT_FORBIDDEN_OPENER: 'CONTENT_FORBIDDEN_OPENER',
  CONTENT_BASIS_COGNITIVE_TOO_HIGH: 'CONTENT_BASIS_COGNITIVE_TOO_HIGH',
  CONTENT_EIND_COGNITIVE_TOO_LOW: 'CONTENT_EIND_COGNITIVE_TOO_LOW',
  // Ruis-audit v1.6 T2.1 (C6): emitted when detectCognitiveLevel() cannot
  // classify a question (formerly defaulted silently to 'recall').
  CONTENT_COGNITIVE_LEVEL_UNKNOWN: 'CONTENT_COGNITIVE_LEVEL_UNKNOWN',
  /**
   * ROLLUP (Sprint 6 — PLUS-cognitive refactor): All Plus-tier inadequacy
   * findings are now emitted under a single canonical code with
   * `metadata.reason` carrying the discriminator. Replaces five legacy codes
   * (CONTENT_PLUS_COGNITIVE_TOO_LOW, CONTENT_PLUS_DIFFICULTY_TOO_LOW,
   * CONTENT_PLUS_MISSING_STEPS, CONTENT_PLUS_FEW_STEPS,
   * CONTENT_PLUS_SINGLE_STEP).
   *
   * `metadata.reason` ∈ {
   *   'cognitive_too_low'   — detected cognitive level below plus minimum (coach)
   *   'difficulty_too_low'  — citoDifficulty below plus minimum (coach)
   *   'missing_steps'       — feedback.solutionSteps absent on plus tier (coach)
   *   'few_steps'           — fewer than required solution steps (info)
   *   'single_step'         — question reads as single-step on plus tier (info)
   * }
   *
   * Admin UI groups primary by `code`, secondary by `metadata.reason`.
   * See computeCodeFrequencyReport — `reasonBreakdown` field.
   */
  CONTENT_PLUS_INADEQUATE: 'CONTENT_PLUS_INADEQUATE',
  // NOTE: the five legacy CONTENT_PLUS_* codes below were removed in the
  // rollup refactor. Old snapshots will still render them (as "vanished" in
  // diffSnapshots). Their semantics now live as `metadata.reason` on
  // CONTENT_PLUS_INADEQUATE.
  //   CONTENT_PLUS_COGNITIVE_TOO_LOW  → reason: 'cognitive_too_low'
  //   CONTENT_PLUS_DIFFICULTY_TOO_LOW → reason: 'difficulty_too_low'
  //   CONTENT_PLUS_MISSING_STEPS      → reason: 'missing_steps'
  //   CONTENT_PLUS_FEW_STEPS          → reason: 'few_steps'
  //   CONTENT_PLUS_SINGLE_STEP        → reason: 'single_step'
  // (legacy CITO range/band-mismatch codes were folded into CONTENT_CITO_ISSUE
  // via metadata.reason — see CONTENT_CITO_ISSUE above for the reason map.)
  // (legacy CONTENT_WEAK_DISTRACTOR_* codes were folded into
  // CONTENT_DISTRACTOR_QUALITY via metadata.reason — see above.)
  CONTENT_QUESTION_SENTENCE_TOO_LONG: 'CONTENT_QUESTION_SENTENCE_TOO_LONG',

  /**
   * Q2Q-LEZEN S-LEZ-04/05 (1 juni 2026). Emitted by
   * `interactivePayloadChecks.validateInteractivePayload` when a
   * `text_highlight` payload (range-mode or `tap_referent`-mode) has
   * indices/offsets that the renderer cannot interpret.
   *
   * `metadata.reason` ∈ {
   *   'interactive_payload_invalid_range'   — range offsets out of bounds
   *                                           or inverted (HARD coach REVIEW)
   *   'interactive_payload_offset_drift'   — declared `text` does not match
   *                                           `passage.slice(start, end)`
   *                                           (SOFT info NEAR_LOW — survives
   *                                           renderer tolerance but signals
   *                                           authoring drift)
   *   'interactive_payload_invalid_index'  — `tap_referent` pronounIndex /
   *                                           correctIndex / candidateIndices
   *                                           out of token range, or
   *                                           correctIndex ∉ candidateIndices
   *                                           (HARD coach REVIEW —
   *                                           unanswerable in production)
   * }
   */
  CONTENT_INTERACTIVE_PAYLOAD: 'CONTENT_INTERACTIVE_PAYLOAD',

  /**
   * Q2Q-LEZEN S-LEZ-01 (1 juni 2026). Emitted by
   * `fillInBreadthChecks.validateFillInBreadth` when a `fill_in` question
   * declares fewer than the recommended number of accepted-answer variants.
   * Soft signal (info / NEAR_LOW) — many fill_in seeds are legitimately
   * strict, but a value below the floor is the most reliable predictor of
   * false negatives on didactically valid student input.
   *
   * `metadata.reason` ∈ {
   *   'fill_in_too_narrow'  — acceptedAnswers.length < FLOOR (default 4)
   * }
   */
  CONTENT_FILL_IN_NARROW: 'CONTENT_FILL_IN_NARROW',

  /**
   * Q2Q-REK Batch 3 / S-1 (juni 2026). Shadow-mode signal: a non-options-
   * bearing question type (fill_in, step_solver, strategy_solver, ordering,
   * categorize, …) carries a `feedback.distractorExplanations` map with
   * numeric keys ('0','1','2','3'). That field is only consumed by
   * options-bearing renderers (multiple_choice, true_false_series,
   * comparison_judge); on other types it is dead metadata — never read,
   * usually a leftover from AI-generated seeds copied from MC templates.
   *
   * Always emitted as `info` (never blocks). Surfaces silent data-quality
   * drift across ALL modules in one pass — see ARCHITECTURAL_PEER_REVIEW_
   * PLAN_2026.md §F0.B (shadow → ramp → enforce). When per-type enforcement
   * is set to 'shadow' via governanceConfigStore, applyEnforcementModes
   * additionally records the finding to shadowFindingsStore.
   */
  CONTENT_DEAD_DISTRACTOR_METADATA: 'CONTENT_DEAD_DISTRACTOR_METADATA',

  // ── Visual Stage (Stage 4) ─────────────────────────────────────────────
  VISUAL_STIMULUS_REQUIRED: 'VISUAL_STIMULUS_REQUIRED',
  VISUAL_SUPPORT_RECOMMENDED: 'VISUAL_SUPPORT_RECOMMENDED',
  VISUAL_STIMULUS_INVALID_TYPE: 'VISUAL_STIMULUS_INVALID_TYPE',
  VISUAL_SUPPORT_INVALID_TYPE: 'VISUAL_SUPPORT_INVALID_TYPE',
  VISUAL_ANSWER_LEAKAGE: 'VISUAL_ANSWER_LEAKAGE',
  VISUAL_STIMULUS_TYPE_MISMATCH: 'VISUAL_STIMULUS_TYPE_MISMATCH',
  VISUAL_SUPPORT_TYPE_MISMATCH: 'VISUAL_SUPPORT_TYPE_MISMATCH',
  VISUAL_UNKNOWN_AID_TYPE: 'VISUAL_UNKNOWN_AID_TYPE',
  VISUAL_DATA_SCHEMA_VIOLATION: 'VISUAL_DATA_SCHEMA_VIOLATION',
  VISUAL_SUPPORT_MISSING_SHOW_WHEN: 'VISUAL_SUPPORT_MISSING_SHOW_WHEN',
  VISUAL_SUPPORT_SHOW_WHEN_MISMATCH: 'VISUAL_SUPPORT_SHOW_WHEN_MISMATCH',
  VISUAL_STIMULUS_INCONSISTENT_IN_FAMILY:
    'VISUAL_STIMULUS_INCONSISTENT_IN_FAMILY',
  /**
   * Coach signal when ALL supportAids on a question are type "rule" (text-only).
   * Hints that the question lacks a didactic visual scaffold.
   */
  VISUAL_SUPPORT_RULE_ONLY: 'VISUAL_SUPPORT_RULE_ONLY',
  VISUAL_RULEBOOK_HINT_STIMULUS_MISMATCH:
    'VISUAL_RULEBOOK_HINT_STIMULUS_MISMATCH',
  VISUAL_RULEBOOK_HINT_SUPPORT_MISMATCH:
    'VISUAL_RULEBOOK_HINT_SUPPORT_MISMATCH',
  /**
   * Family-level signal: emitted by the batch consistency check when ≥80%
   * of the questions in a family use a visual that disagrees with the
   * rulebook hint (with minimum 3 questions). Hints that the rulebook
   * itself may need adjustment, or that the whole family needs a
   * coordinated migration — distinct from per-question drift.
   */
  VISUAL_RULEBOOK_HINT_FAMILY_DRIFT: 'VISUAL_RULEBOOK_HINT_FAMILY_DRIFT',

  // ── Type Stage — New interactive types ─────────────────────────────────
  TYPE_TRUE_FALSE_TOO_FEW: 'TYPE_TRUE_FALSE_TOO_FEW',
  TYPE_TRUE_FALSE_INVALID_STATEMENT: 'TYPE_TRUE_FALSE_INVALID_STATEMENT',
  TYPE_TRUE_FALSE_ANSWERS_MISMATCH: 'TYPE_TRUE_FALSE_ANSWERS_MISMATCH',
  TYPE_TRUE_FALSE_INVALID_ANSWER: 'TYPE_TRUE_FALSE_INVALID_ANSWER',
  TYPE_CATEGORIZE_TOO_FEW_CATEGORIES: 'TYPE_CATEGORIZE_TOO_FEW_CATEGORIES',
  TYPE_CATEGORIZE_TOO_FEW_ITEMS: 'TYPE_CATEGORIZE_TOO_FEW_ITEMS',
  TYPE_CATEGORIZE_INVALID_ITEM: 'TYPE_CATEGORIZE_INVALID_ITEM',
  TYPE_CATEGORIZE_INVALID_MAPPING: 'TYPE_CATEGORIZE_INVALID_MAPPING',
  TYPE_ERROR_SPOTTING_NO_TEXT: 'TYPE_ERROR_SPOTTING_NO_TEXT',
  TYPE_ERROR_SPOTTING_NO_ERRORS: 'TYPE_ERROR_SPOTTING_NO_ERRORS',
  TYPE_ERROR_SPOTTING_INVALID_ERROR: 'TYPE_ERROR_SPOTTING_INVALID_ERROR',
  TYPE_GRID_SHADE_INVALID_DIMS: 'TYPE_GRID_SHADE_INVALID_DIMS',
  TYPE_GRID_SHADE_INVALID_TARGET: 'TYPE_GRID_SHADE_INVALID_TARGET',
  TYPE_GRID_SHADE_INVALID_MODE: 'TYPE_GRID_SHADE_INVALID_MODE',
  TYPE_STEP_SOLVER_NO_STEPS: 'TYPE_STEP_SOLVER_NO_STEPS',
  TYPE_STEP_SOLVER_INVALID_STEP: 'TYPE_STEP_SOLVER_INVALID_STEP',
  TYPE_SENTENCE_PARSER_NO_WORDS: 'TYPE_SENTENCE_PARSER_NO_WORDS',
  TYPE_SENTENCE_PARSER_NO_LABELS: 'TYPE_SENTENCE_PARSER_NO_LABELS',
  TYPE_SENTENCE_PARSER_INVALID_ASSIGNMENT:
    'TYPE_SENTENCE_PARSER_INVALID_ASSIGNMENT',
  TYPE_COMPARISON_TOO_FEW_ITEMS: 'TYPE_COMPARISON_TOO_FEW_ITEMS',
  TYPE_COMPARISON_NO_CRITERION: 'TYPE_COMPARISON_NO_CRITERION',
  TYPE_COMPARISON_INVALID_INDEX: 'TYPE_COMPARISON_INVALID_INDEX',
  // Multiplication grid
  TYPE_MULT_GRID_INVALID_MODE: 'TYPE_MULT_GRID_INVALID_MODE',
  TYPE_MULT_GRID_INVALID_BASE: 'TYPE_MULT_GRID_INVALID_BASE',
  TYPE_MULT_GRID_INVALID_RANGE: 'TYPE_MULT_GRID_INVALID_RANGE',
  TYPE_MULT_GRID_NO_HIDDEN: 'TYPE_MULT_GRID_NO_HIDDEN',
  TYPE_MULT_GRID_HIDDEN_OOB: 'TYPE_MULT_GRID_HIDDEN_OOB',
  TYPE_MULT_GRID_INVALID_ROWS: 'TYPE_MULT_GRID_INVALID_ROWS',
  TYPE_MULT_GRID_INVALID_COLS: 'TYPE_MULT_GRID_INVALID_COLS',
  TYPE_MULT_GRID_INVALID_HIDDEN: 'TYPE_MULT_GRID_INVALID_HIDDEN',
  // Estimation slider
  TYPE_EST_SLIDER_NO_MIN: 'TYPE_EST_SLIDER_NO_MIN',
  TYPE_EST_SLIDER_NO_MAX: 'TYPE_EST_SLIDER_NO_MAX',
  TYPE_EST_SLIDER_MIN_GTE_MAX: 'TYPE_EST_SLIDER_MIN_GTE_MAX',
  TYPE_EST_SLIDER_NO_VALUE: 'TYPE_EST_SLIDER_NO_VALUE',
  TYPE_EST_SLIDER_VALUE_OOB: 'TYPE_EST_SLIDER_VALUE_OOB',
  TYPE_EST_SLIDER_TOLERANCE: 'TYPE_EST_SLIDER_TOLERANCE',
  // Thermometer slider
  TYPE_THERM_NO_MIN: 'TYPE_THERM_NO_MIN',
  TYPE_THERM_NO_MAX: 'TYPE_THERM_NO_MAX',
  TYPE_THERM_MIN_GTE_MAX: 'TYPE_THERM_MIN_GTE_MAX',
  TYPE_THERM_INVALID_STEP: 'TYPE_THERM_INVALID_STEP',
  TYPE_THERM_ANSWER_OOB: 'TYPE_THERM_ANSWER_OOB',
  // Verb conjugation flow
  TYPE_VERB_FLOW_NO_VERB: 'TYPE_VERB_FLOW_NO_VERB',
  TYPE_VERB_FLOW_NO_STEPS: 'TYPE_VERB_FLOW_NO_STEPS',
  TYPE_VERB_FLOW_EMPTY_STEPS: 'TYPE_VERB_FLOW_EMPTY_STEPS',
  TYPE_VERB_FLOW_INVALID_STEP: 'TYPE_VERB_FLOW_INVALID_STEP',
  // Sentence error judge
  TYPE_SENT_ERROR_NO_SENTENCES: 'TYPE_SENT_ERROR_NO_SENTENCES',
  TYPE_SENT_ERROR_TOO_FEW: 'TYPE_SENT_ERROR_TOO_FEW',
  TYPE_SENT_ERROR_INVALID: 'TYPE_SENT_ERROR_INVALID',
  TYPE_SENT_ERROR_NO_CORRECTION: 'TYPE_SENT_ERROR_NO_CORRECTION',
  TYPE_SENT_ERROR_NONE_MARKED: 'TYPE_SENT_ERROR_NONE_MARKED',
  TYPE_SENT_ERROR_MULTIPLE: 'TYPE_SENT_ERROR_MULTIPLE',

  // ── Support Stage (Stage 5) ────────────────────────────────────────────
  SUPPORT_FAMILY_NOT_IN_REGISTRY: 'SUPPORT_FAMILY_NOT_IN_REGISTRY',
  SUPPORT_HINTS_EMPTY: 'SUPPORT_HINTS_EMPTY',
  // NOTE: SUPPORT_HINTS_PRESENT was removed — never emitted by any stage
  // (confirmed dead during 2026 governance refactor; see
  // docs/audits/GOVERNANCE_DOMAIN_CONSOLIDATED_AUDIT_2026.md). Snapshots
  // store codes as raw strings, so old snapshots still decode fine.
  // NOTE: SUPPORT_EXPLANATION_REQUIRED was removed — it duplicated
  // SCHEMA_MISSING_EXPLANATION (same field: feedback.explanationText,
  // same condition: missing/empty). Schema stage is now SSOT for
  // "explanation absent" (blocker); content stage owns "too short";
  // support stage only owns registry-level defaultExplanation checks.
  SUPPORT_VISUAL_REQUIRED: 'SUPPORT_VISUAL_REQUIRED',
  SUPPORT_FAMILY_LOW_QUESTION_COUNT: 'SUPPORT_FAMILY_LOW_QUESTION_COUNT',

  /**
   * ROLLUP (Sprint 8 — defaultExplanation refactor): registry-level
   * defaultExplanation quality findings now emit under one canonical code
   * with `metadata.reason` carrying the discriminator. Replaces three legacy
   * codes (SUPPORT_EXPLANATION_MISSING, _TOO_SHORT, _TOO_LONG).
   *
   * `metadata.reason` ∈ {
   *   'missing'    — registry entry exists but defaultExplanation absent (coach)
   *   'too_short'  — below SUPPORT_THRESHOLDS.MIN_EXPLANATION_CHARS (coach)
   *   'too_long'   — above SUPPORT_THRESHOLDS.MAX_EXPLANATION_CHARS (info)
   * }
   *
   * Admin UI groups primary by `code`, secondary by `metadata.reason`.
   * Same pattern as CONTENT_CITO_ISSUE / CONTENT_DISTRACTOR_QUALITY.
   */
  SUPPORT_EXPLANATION_QUALITY: 'SUPPORT_EXPLANATION_QUALITY',
  // NOTE: SUPPORT_EXPLANATION_MISSING / _TOO_SHORT / _TOO_LONG were removed —
  // all three were folded into SUPPORT_EXPLANATION_QUALITY rollup (Sprint 8)
  // with metadata.reason ∈ { 'missing', 'too_short', 'too_long' }.

  /**
   * ROLLUP (Sprint 8 — cito-spread refactor): citoDifficulty-spread findings
   * within a family now emit under one canonical code with `metadata.reason`.
   * Replaces two legacy codes (SUPPORT_FAMILY_CITO_NO_SPREAD,
   * SUPPORT_FAMILY_CITO_LOW_SPREAD).
   *
   * `metadata.reason` ∈ {
   *   'no_spread'  — all family questions share the same citoDifficulty (info)
   *   'low_spread' — spread below SUPPORT_THRESHOLDS.MIN_CITO_SPREAD (coach)
   * }
   *
   * Same rollup pattern as SUPPORT_EXPLANATION_QUALITY.
   */
  SUPPORT_FAMILY_CITO_SPREAD: 'SUPPORT_FAMILY_CITO_SPREAD',
  // NOTE: SUPPORT_FAMILY_CITO_NO_SPREAD / _LOW_SPREAD were removed —
  // both folded into SUPPORT_FAMILY_CITO_SPREAD rollup (Sprint 8) with
  // metadata.reason ∈ { 'no_spread', 'low_spread' }.

  SUPPORT_FAMILY_NUMBER_PATTERN_COLLAPSE:
    'SUPPORT_FAMILY_NUMBER_PATTERN_COLLAPSE',
  SUPPORT_FAMILY_LINGUISTIC_TEMPLATE_COLLAPSE:
    'SUPPORT_FAMILY_LINGUISTIC_TEMPLATE_COLLAPSE',

  // ── Seed Stage (Stage 5b) ─────────────────────────────────────────────
  SEED_TIER_AUTOCORRECTED: 'SEED_TIER_AUTOCORRECTED',
  SEED_TIER_INVALID: 'SEED_TIER_INVALID',
  SEED_TIER_OUT_OF_RANGE: 'SEED_TIER_OUT_OF_RANGE',
  SEED_SKILL_AUTOCORRECTED: 'SEED_SKILL_AUTOCORRECTED',
  SEED_SUBSKILL_AUTOCORRECTED: 'SEED_SUBSKILL_AUTOCORRECTED',
  // NOTE: SEED_REJECTED was removed — never emitted by any stage. The
  // architecture doc referenced it but no code path ever produced it.
  /**
   * Phase 2 (Strak-Trek): seeds MUST declare `pedagogy.dok` (a DOK level
   * 1-4). Owned by the seed stage so that "seed completeness" lives in one
   * place; the pedagogy stage only checks alignment (floor/ceiling) when
   * dok IS defined. Severity:
   *   - blocker (severity 75) when `enforcePedagogicalContract` flag is on
   *   - coach   (severity 50 / 35 for tier < 5) otherwise
   * Single signal for "DOK ontbreekt" — replaces the legacy
   * PEDAGOGY_DOK_MISSING code that was removed in the cleanup pass.
   */
  // NOTE: SEED_DOK_AUTO_ESTIMATED was folded into SEED_DOK_MISSING.metadata
  // (cleanup pass). The dokAutoTagger heuristic estimate now lives as
  // structured metadata on the single SEED_DOK_MISSING issue instead of
  // a separate 1-to-1 info issue. Admin UI reads issue.metadata to
  // render the "apply suggestion" action.
  SEED_DOK_MISSING: 'SEED_DOK_MISSING',

  // ── Diversity Stage (Stage 5c) — QUESTION_TYPE_DIVERSITY_RULES ─────
  DIVERSITY_TOO_FEW_TYPES: 'DIVERSITY_TOO_FEW_TYPES',
  DIVERSITY_NO_INTERACTIVE: 'DIVERSITY_NO_INTERACTIVE',
  DIVERSITY_LOW_VARIETY: 'DIVERSITY_LOW_VARIETY',
  DIVERSITY_NO_DOK3_TYPE: 'DIVERSITY_NO_DOK3_TYPE',

  /**
   * Rollup code (Sprint 7 — diversity refactor).
   * Replaces:
   *   - DIVERSITY_MC_OVERUSE       → reason: 'multiple_choice'
   *   - DIVERSITY_FILL_IN_OVERUSE  → reason: 'fill_in'
   *   - (new)                      → reason: 'true_false_series'
   * Metadata shape:
   *   { reason: QuestionType, percent: number, threshold: number, count: number, total: number }
   * Per-type thresholds live in diversityStage.ts (TYPE_OVERUSE_THRESHOLDS).
   * Rationale: all "type X mag niet > N% van de family zijn"-checks share
   * one code + a metadata.reason discriminator (same pattern as
   * CONTENT_CITO_ISSUE / CONTENT_DISTRACTOR_QUALITY / CONTENT_PLUS_INADEQUATE).
   */
  DIVERSITY_TYPE_OVERUSE: 'DIVERSITY_TYPE_OVERUSE',
  // NOTE: DIVERSITY_MC_OVERUSE / _FILL_IN_OVERUSE were removed — both folded
  // into DIVERSITY_TYPE_OVERUSE (Sprint 7) with metadata.reason carrying the
  // question type. Per-type caps live in diversityStage.ts.
  // NOTE: DIVERSITY_CONSECUTIVE_SAME was removed — false signal. It checked
  // seed-JSON ordering, but the adaptive engine (v11.2) + modeSelectors
  // shuffle and dynamically pick questions, so seed order never reaches
  // the learner.

  // ── Pedagogy Stage (Stage 3b) — PEDAGOGICAL_CONTRACT_SPEC §8b ──────
  // NOTE: PEDAGOGY_DOK_MISSING was removed — completeness is now owned by
  // SEED_DOK_MISSING (seedStage) so there is one signal per check.
  PEDAGOGY_DOK_TOO_LOW: 'PEDAGOGY_DOK_TOO_LOW',
  PEDAGOGY_DOK_TOO_HIGH: 'PEDAGOGY_DOK_TOO_HIGH',
  // NOTE: PEDAGOGY_DOK_AUTO_ESTIMATED was removed in the system-hardening
  // pass — never emitted (the canonical auto-estimate code is the
  // SEED_DOK_AUTO_ESTIMATED issue produced by seedStage).
  PEDAGOGY_INDICATOR_LEAKS_ANSWER: 'PEDAGOGY_INDICATOR_LEAKS_ANSWER',
  PEDAGOGY_DISTRACTORS_UNTAGGED: 'PEDAGOGY_DISTRACTORS_UNTAGGED',
  PEDAGOGY_DISTRACTORS_PARTIAL: 'PEDAGOGY_DISTRACTORS_PARTIAL',
  PEDAGOGY_REGEX_FALLBACK: 'PEDAGOGY_REGEX_FALLBACK',
  // NOTE: PEDAGOGY_MISCONCEPTION_MISSING was removed — never emitted by any
  // stage. The "partial misconception tagging" signal lives in
  // PEDAGOGY_DISTRACTORS_PARTIAL; admin UI reads that code instead.
  PEDAGOGY_MISCONCEPTION_UNKNOWN: 'PEDAGOGY_MISCONCEPTION_UNKNOWN',
  /**
   * MET Q2Q consolidatie / SSOT §3 amendement (3 juni 2026). Shadow-mode
   * signal: a seed uses a misconception-ID whose body (after the module
   * prefix) is English. Per SSOT §3 regel 3, Dutch is the canonical
   * language for misconception-IDs; English IDs are legacy and slated for
   * migration. The per-module counter in `MISCONCEPTION_REGISTRY_SEED_
   * SYNC_2026.md` §6 tracks the cleanup progress.
   *
   * Always emitted as `info` (never blocks, never escalates via the
   * enforce-flag). Pure telemetry while the cleanup proceeds — its job
   * is to surface new EN-ID drift during the migration so the §6 counter
   * cannot silently grow. Conforms to ARCHITECTURAL_PEER_REVIEW_PLAN_2026
   * §FG4 shadow-mode + §F0.B pattern.
   *
   * Exception clause (SSOT §3): pure loanword misconceptions where no
   * Dutch equivalent exists are ALLOWED to keep an English-language ID.
   * The check uses a tiny allowlist of `loanword`-style prefixes
   * (`spr.loanword-*`, `spr.engels-spelling-*`, `sww.engels-grammatica-*`)
   * that suppresses the finding. Future work: promote the allowlist to a
   * standalone `loanwordAllowlist.ts` registry per the §6.5
   * definition-of-done.
   */
  PEDAGOGY_MISCONCEPTION_EN_LEGACY: 'PEDAGOGY_MISCONCEPTION_EN_LEGACY',
  PEDAGOGY_STRUCTURED_DATA_MISSING: 'PEDAGOGY_STRUCTURED_DATA_MISSING',
  /**
   * Q2Q-REK Batch 3 / S-2 (juni 2026). Shadow-mode signal: a non-MC
   * question type carries `pedagogy.misconceptionTags[]` entries with an
   * `optionIndex` property. `optionIndex` only makes sense for options-
   * bearing types (multiple_choice). On fill_in / step_solver /
   * strategy_solver / etc. the index points to nothing — dead metadata
   * that survived AI-generation drift.
   *
   * Always emitted as `info` (never blocks). Cross-module — fires for any
   * question whose type lacks an `options[]` array. See ARCHITECTURAL_PEER_
   * REVIEW_PLAN_2026.md §F0.B (shadow-mode pattern).
   */
  PEDAGOGY_DEAD_OPTION_INDEX: 'PEDAGOGY_DEAD_OPTION_INDEX',
  /**
   * Coach signal: the question's questionType appears in a matching
   * rulebook `familyTypeHints[].discouragedTypes` entry. Emitted by
   * `pedagogyStage.checkFamilyTypeHint`. Surfaces drift between seed
   * authorship and the per-family didactic preference, without blocking.
   * Severity LOW at tier < HIGH_STAKES_TIER, REVIEW at tier ≥ 9.
   * Always coach — never a blocker — so existing seeds remain valid while
   * the hint registry grows. Use it to drive admin-UI suggestions.
   */
  PEDAGOGY_FAMILY_TYPE_DISCOURAGED: 'PEDAGOGY_FAMILY_TYPE_DISCOURAGED',

  // ── Cross-stage: sub-validator crash isolation (R7, Sprint 5) ──────
  /**
   * Emitted by runSubValidator when a single sub-validator throws.
   * Allows the parent stage to continue with sibling checks instead of
   * crashing the entire stage. Severity 5 — diagnostic only.
   */
  SUBVALIDATOR_CRASHED: 'SUBVALIDATOR_CRASHED',

  // ── Assessment Health Stage (Stage 6b) — S8 / FG-AssessmentPool ────
  /**
   * Macro-governance: the assessment-eligible pool for a (module, tierBand)
   * slice is below the minimum-viable-size threshold. Diagnostic only —
   * NEVER a blocker. Default kind is `coach` when the pool is under-
   * dimensioned, `info` when healthy-but-borderline.
   *
   * metadata payload:
   *   {
   *     module: ModuleCode,
   *     tierBand: TierBand,
   *     mcEligibleCount: number,
   *     minimumViableSize: number,
   *     comfortSize: number,           // 1.5× minimum
   *     status: 'gap' | 'thin' | 'ok',
   *     remediation?: { needed: number }
   *   }
   *
   * Lives under domain `assessmentPool` (NOT `assessment` or `support`) so
   * existing dashboards filter cleanly. See assessmentHealthStage.ts.
   */
  ASSESSMENT_POOL_CAPACITY: 'ASSESSMENT_POOL_CAPACITY',
  /**
   * Layer B (distribution) — rollup code. Cito-difficulty bucket concentration
   * and DOK-coverage findings emit here with `metadata.reason` as discriminator.
   * Same rollup pattern as CONTENT_CITO_ISSUE, SUPPORT_EXPLANATION_QUALITY,
   * DIVERSITY_TYPE_OVERUSE.
   *
   * `metadata.reason` ∈ {
   *   'cito_concentration' — one cito-bucket holds too large a share of the
   *                          pool (coach when share ≥ 80%, info when ≥ 60%)
   *   'dok_coverage'       — pool covers too few distinct DOK levels
   *                          (coach: basic/intermediate ≥ 2 required,
   *                                  advanced/expert ≥ 3 required)
   * }
   *
   * Admin UI groups primary by `code`, secondary by `metadata.reason`.
   */
  ASSESSMENT_POOL_DISTRIBUTION: 'ASSESSMENT_POOL_DISTRIBUTION',
  /**
   * RESERVED — Layer C (feasibility, later sprint). `assessmentBlueprint
   * Generator` dry-run faalt of degradeert voor een (group, module)
   * combinatie. Dit is de enige Assessment Health-code die `blocker` mag
   * worden — onmogelijkheid is aantoonbaar, geen opinie.
   */
  ASSESSMENT_POOL_FEASIBILITY: 'ASSESSMENT_POOL_FEASIBILITY',
} as const

/** Type-safe issue code */
export type IssueCode = (typeof ISSUE_CODES)[keyof typeof ISSUE_CODES]

/** Validation domains map to pipeline stages */
export type IssueDomain =
  | 'schema' // Stage 1: structural validation
  | 'type' // Stage 1b: question type validation
  | 'graph' // Stage 2: LDG/prerequisite validation
  | 'content' // Stage 3: content quality validation
  | 'pedagogy' // Stage 3b: pedagogical contract validation
  | 'visual' // Stage 4: visual aid validation
  | 'support' // Stage 5: support/hint validation (hints, defaultExplanation, visual hints)
  | 'familyStats' // Stage 5b: family-level content statistics (P1-2 fix, mei 2026) — count, cito-spread, number-pattern, linguistic-template collapse
  | 'diversity' // Stage 5c: family-level question-type diversity (P1-3 fix, mei 2026)
  | 'seed' // Stage 5b: seed consolidation warnings (tier-lock, skill-lock)
  | 'scoring' // Stage 6: score computation
  | 'assessmentPool' // Stage 6b: macro-governance over assessment-eligible pool (S8, mei 2026) — capacity / distribution / feasibility. Diagnostic only, default shadow.
  | 'gate' // Stage 7: gate logic

/** Pipeline stage numbers (1-7) */
export type PipelineStage = 1 | 2 | 3 | 4 | 5 | 6 | 7
